import { useSpareKeyboxRotation } from "@/hooks/useSpareKeyboxRotation"
import { useKeyboxRotation } from "@/hooks/useKeyboxRotation"

export const CALLBACK_TYPES = {
    SPARE_KEY_COMPLETION: "spare-key-completion",
    GUEST_KEY_COMPLETION: "guest-key-completion"
} as const

export type CallbackType = (typeof CALLBACK_TYPES)[keyof typeof CALLBACK_TYPES]

interface CallbackExecution {
    execute: () => Promise<void>
    createCallback: (
        cleaningId: string,
        callbackType: CallbackType
    ) => {
        callbackId: string
        callbackType: CallbackType
    }
}

export const useCallbackExecution = (
    cleaningId: string,
    callbackType?: string,
    callbackId?: string
): CallbackExecution => {
    const spareKeyRotation = useSpareKeyboxRotation(cleaningId)
    const guestKeyRotation = useKeyboxRotation(cleaningId)

    return {
        execute: async () => {
            if (!callbackType || !callbackId) return

            try {
                switch (callbackType) {
                    case CALLBACK_TYPES.SPARE_KEY_COMPLETION:
                        if (
                            spareKeyRotation &&
                            !spareKeyRotation.isLoading &&
                            !spareKeyRotation.error
                        ) {
                            await spareKeyRotation?.rotate()
                        }
                        break
                    case CALLBACK_TYPES.GUEST_KEY_COMPLETION:
                        if (
                            guestKeyRotation &&
                            !guestKeyRotation.isLoading &&
                            !guestKeyRotation.error
                        ) {
                            await guestKeyRotation.rotate()
                        }
                        break
                }
                sessionStorage.removeItem(callbackId)
            } catch (error) {
                console.error(`Failed to execute ${callbackType}:`, error)
                throw error
            }
        },
        createCallback: (cleaningId: string, callbackType: CallbackType) => {
            const callbackId = `${callbackType}-${cleaningId}-${Date.now()}`
            sessionStorage.setItem(callbackId, callbackType)
            return { callbackId, callbackType }
        }
    }
}
